import { useMemo } from 'react';
import { useClinicians } from '@features/organizations/store/hooks';
import { useUserId, useUserInfo } from '@features/user';

import { useOrgScheduleAccess } from './useOrgScheduleAccess';
import { useOrgScheduleFilters } from './useOrgScheduleFilters';

/**
 * Builds a provider_id → provider name map, gated on org view.
 *
 * When org view is inactive the map is empty, so consumers can
 * simply do `providerMap.get(id)` — undefined means "don't render".
 * RTK Query cache means zero extra fetches when the filter bar
 * already triggered the clinicians query.
 */
export function useProviderNameMap(): Map<string, string> {
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive } = useOrgScheduleFilters();
  const isActive = hasOrgScheduleAccess && isOrgViewActive;
  const currentUserId = useUserId();
  const currentUserInfo = useUserInfo();

  const { clinicians } = useClinicians({ skip: !isActive });

  return useMemo(() => {
    const providerNameMap = new Map<string, string>();

    if (isActive) {
      for (const clinician of clinicians) {
        providerNameMap.set(clinician.userId, clinician.name);
      }
    }

    const currentUserName = getCurrentUserDisplayName(currentUserInfo);
    if (currentUserId && currentUserName) {
      providerNameMap.set(currentUserId, currentUserName);
    }

    return providerNameMap;
  }, [isActive, clinicians, currentUserId, currentUserInfo]);
}

const getCurrentUserDisplayName = (
  userInfo: { name?: string; firstName?: string; lastName?: string; email?: string } | undefined,
): string | null => {
  if (!userInfo) return null;
  if (userInfo.name) return userInfo.name;

  const composed = `${userInfo.firstName ?? ''} ${userInfo.lastName ?? ''}`.trim();
  return composed || userInfo.email || null;
};
